import {
  ActionButtonContainer,
  ActionButton,
} from "components/AgGrid/ActionButton";

import React from "react";

import {
  faDownload,
  faEye,
  faEyeSlash,
  faPen,
  faTableColumns,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import type { SideLoadedBusinessGridContext } from "components/Business/SideLoadedBusinesses/useSideLoadedBusinessGridContext";
import type { GridSideLoadedBusiness } from "selectors/sideLoadedBusinesses";

// Compared against the grouped column's field rather than importing its colId,
// which would make this module and the column definitions circular.
const DATASET_FIELD: keyof GridSideLoadedBusiness = "datasetLabel";

interface SideLoadedBusinessActionsRendererProps {
  context: SideLoadedBusinessGridContext;
  data?: SideLoadedBusiness;
  node: {
    group: boolean;
    key?: string | null;
    rowGroupColumn?: { getColDef: () => { field?: string } } | null;
  };
}

export function SideLoadedBusinessActionsRenderer({
  context,
  data,
  node,
}: SideLoadedBusinessActionsRendererProps) {
  const {
    canDeleteDataSet,
    canIgnore,
    handleSelectAndCompare,
    handleSetIgnored,
    handleDeleteDataSet,
    selectActionLabel,
    handleEditTemplate,
    handleRenameDataSet,
  } = context;

  if (node?.group) {
    if (node.rowGroupColumn?.getColDef().field !== DATASET_FIELD) {
      return null;
    }
    // Absent while the data sets load, when the label falls back to the bare name.
    const dataSet = node.key ? context.dataSetsByLabel[node.key] : null;
    if (!dataSet || !canDeleteDataSet) {
      return null;
    }
    return (
      <ActionButtonContainer>
        {handleEditTemplate && (
          <ActionButton
            onClick={() => handleEditTemplate(dataSet)}
            data-tour="editSideLoadedDataTemplateFromGrid"
            title={`Change what each column of "${dataSet.name}" means.`}
          >
            <FontAwesomeIcon icon={faTableColumns} />
            &nbsp;Template
          </ActionButton>
        )}
        {handleRenameDataSet && (
          <ActionButton
            onClick={() => handleRenameDataSet(dataSet)}
            data-tour="renameSideLoadedDataSetFromGrid"
            title={`Rename the "${dataSet.name}" data set.`}
          >
            <FontAwesomeIcon icon={faPen} />
            &nbsp;Rename
          </ActionButton>
        )}
        <ActionButton
          onClick={() => handleDeleteDataSet(dataSet)}
          data-tour="deleteSideLoadedDataSetFromGrid"
          title={`Delete the "${dataSet.name}" data set and all of its records.`}
        >
          <FontAwesomeIcon icon={faTrash} />
          &nbsp;Delete Data
        </ActionButton>
      </ActionButtonContainer>
    );
  }

  if (!data) {
    return null;
  }

  const { id, isIgnored } = data;

  let ignoreTitle = isIgnored
    ? "Show this entry in suggestions again."
    : "Stop showing this entry in suggestions.";
  if (!canIgnore) {
    ignoreTitle = "You don't have permission to change this data set.";
  }

  return (
    <ActionButtonContainer>
      <ActionButton
        onClick={() => handleSelectAndCompare(id)}
        data-tour="selectAndCompare"
      >
        <FontAwesomeIcon icon={faDownload} />
        &nbsp;{selectActionLabel}
      </ActionButton>
      <ActionButton
        onClick={() => handleSetIgnored(id, !isIgnored)}
        data-tour="ignoreSideLoadedBusiness"
        disabled={!canIgnore}
        title={ignoreTitle}
      >
        <FontAwesomeIcon icon={isIgnored ? faEye : faEyeSlash} />
        &nbsp;{isIgnored ? "Restore" : "Ignore"}
      </ActionButton>
    </ActionButtonContainer>
  );
}
